import { type LucideIcon } from "lucide-react";
import { Link } from "react-router-dom";

import {
  SidebarGroup,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { pathMatchesItem } from "@/config/sidebar-nav";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

/**
 * A single flat nav row. The redesigned sidebar (1c) has no collapsible
 * parents: every feature is a leaf link, so nothing takes two clicks.
 */
export type NavMainItem = {
  title: string;
  url: string;
  icon?: LucideIcon;
  /** Shown next to the title (e.g. Beta). */
  titleBadge?: string;
  /** Pre-computed active state; falls back to a prefix match on `url`. */
  isActive?: boolean;
};

/**
 * Row styling from the platform sketch: 15/500, padding 11 14, radius 12,
 * icon 20, gap 13; the active row is a raised card in primary ink. Shared
 * with the pinned Settings row in the sidebar footer. Tokens only.
 */
export const navRowClassName = cn(
  "h-auto gap-[13px] rounded-g-md px-[14px] py-[11px] text-[15px] font-medium text-g-ink-700",
  "transition-colors duration-150 ease-out",
  "hover:bg-g-bg-card hover:text-g-ink-900",
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-g-ring",
  "data-[active=true]:bg-g-bg-card data-[active=true]:font-[650] data-[active=true]:text-g-primary-press",
  "data-[active=true]:shadow-g-card data-[active=true]:ring-1 data-[active=true]:ring-g-line-strong",
  "[&>svg]:size-5 [&>svg]:shrink-0",
  "group-data-[collapsible=icon]:justify-center group-data-[collapsible=icon]:px-0",
);

function NavTitleWithOptionalBadge({
  title,
  titleBadge,
}: {
  title: string;
  titleBadge?: string;
}) {
  return (
    <span className="flex min-w-0 flex-1 items-center gap-1.5">
      <span className="truncate">{title}</span>
      {titleBadge ? (
        <Badge
          variant="secondary"
          className="h-5 shrink-0 px-1.5 py-0 text-[10px] font-medium"
        >
          {titleBadge}
        </Badge>
      ) : null}
    </span>
  );
}

export function NavMain({
  items,
  pathname,
  groupLabel = null,
  hairline = false,
}: {
  items: NavMainItem[];
  pathname: string;
  /** Optional uppercase group heading (KONTROLLPANEL, REKRYTERING). */
  groupLabel?: string | null;
  /** Draw the sketch's gradient hairline above the heading. */
  hairline?: boolean;
}) {
  return (
    <SidebarGroup className="px-0 py-1">
      {groupLabel ? (
        <SidebarGroupLabel
          className={cn(
            "h-auto flex-col items-stretch gap-2 rounded-none px-[14px] pb-1.5 text-[11px] font-extrabold uppercase tracking-[0.12em] text-g-ink-400",
            hairline ? "pt-4" : "pt-3",
          )}
        >
          {hairline ? (
            <span
              aria-hidden="true"
              className="block h-px w-full bg-gradient-to-r from-g-primary-muted to-transparent"
            />
          ) : null}
          <span>{groupLabel}</span>
        </SidebarGroupLabel>
      ) : null}
      <SidebarMenu className="gap-1">
        {items.map((item) => {
          const Icon = item.icon;
          const active = item.isActive ?? pathMatchesItem(pathname, item.url);
          const tooltip = item.titleBadge
            ? `${item.title} (${item.titleBadge})`
            : item.title;
          return (
            <SidebarMenuItem key={item.url}>
              <SidebarMenuButton
                tooltip={tooltip}
                isActive={active}
                asChild
                className={navRowClassName}
              >
                <Link to={item.url} aria-current={active ? "page" : undefined}>
                  {Icon ? <Icon aria-hidden="true" /> : null}
                  <NavTitleWithOptionalBadge
                    title={item.title}
                    titleBadge={item.titleBadge}
                  />
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
